import { useCallback, useRef, useState } from 'react';
import type { CropArea } from './types';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('useCropCanvas');

interface UseCropCanvasProps {
  onSave: (croppedBlob: Blob) => void | Promise<void>;
  maxLongSide?: number;
  /** JPEG output size budget in bytes. Defaults to 900KB. */
  maxFileSize?: number;
}

interface UseCropCanvasReturn {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  isSaving: boolean;
  handleSave: (
    imageElement: HTMLImageElement | null,
    cropArea: CropArea
  ) => Promise<void>;
}

export function useCropCanvas({
  onSave,
  maxLongSide,
  maxFileSize = 900 * 1024,
}: UseCropCanvasProps): UseCropCanvasReturn {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(
    async (imageElement: HTMLImageElement | null, cropArea: CropArea) => {
      const canvas = canvasRef.current;

      if (!canvas || !imageElement) {
        console.error('Canvas or image not ready');
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get canvas context');
        return;
      }

      setIsSaving(true);

      try {
        const pixelMultiple = 16;
        let targetWidth: number;
        let targetHeight: number;

        if (maxLongSide) {
          // Longest-side algorithm (for angles: 2048px), only downscale
          const longestSide = Math.max(cropArea.width, cropArea.height);
          if (longestSide <= maxLongSide) {
            targetWidth = Math.max(
              pixelMultiple,
              Math.round(cropArea.width / pixelMultiple) * pixelMultiple
            );
            targetHeight = Math.max(
              pixelMultiple,
              Math.round(cropArea.height / pixelMultiple) * pixelMultiple
            );
          } else {
            const scale = maxLongSide / longestSide;
            targetWidth = Math.max(
              pixelMultiple,
              Math.round((cropArea.width * scale) / pixelMultiple) *
                pixelMultiple
            );
            targetHeight = Math.max(
              pixelMultiple,
              Math.round((cropArea.height * scale) / pixelMultiple) *
                pixelMultiple
            );
          }
        } else {
          // Default: 1 megapixel target
          const aspectRatio = cropArea.width / cropArea.height;
          const targetPixels = 1000000;
          targetWidth =
            Math.round(Math.sqrt(targetPixels * aspectRatio) / pixelMultiple) *
            pixelMultiple;
          targetHeight =
            Math.round(targetWidth / aspectRatio / pixelMultiple) *
            pixelMultiple;
          const actualPixels = targetWidth * targetHeight;
          if (actualPixels > targetPixels) {
            const scale = Math.sqrt(targetPixels / actualPixels);
            targetWidth =
              Math.round((targetWidth * scale) / pixelMultiple) * pixelMultiple;
            targetHeight =
              Math.round((targetHeight * scale) / pixelMultiple) *
              pixelMultiple;
          }
        }

        // Set canvas size to target dimensions
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Enable high quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw cropped and resized image
        ctx.drawImage(
          imageElement,
          cropArea.x,
          cropArea.y,
          cropArea.width,
          cropArea.height,
          0,
          0,
          targetWidth,
          targetHeight
        );

        // Convert to blob with quality optimization
        let quality = 0.8;

        const tryQuality = async (q: number): Promise<Blob | null> => {
          return new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), 'image/jpeg', q);
          });
        };

        let blob: Blob | null = null;
        while (quality >= 0.3) {
          blob = await tryQuality(quality);
          if (blob && blob.size <= maxFileSize) {
            break;
          }
          quality -= 0.1;
        }

        if (blob) {
          logger.info('Cropped image created:', {
            targetDimensions: `${targetWidth}x${targetHeight}`,
            pixelCount: targetWidth * targetHeight,
            fileSize: blob.size,
            quality,
            type: blob.type,
          });
          await onSave(blob);
        } else {
          console.error('Failed to create blob from canvas');
        }
        setIsSaving(false);
      } catch (error) {
        console.error('Error saving image:', error);
        setIsSaving(false);
      }
    },
    [onSave, maxLongSide, maxFileSize]
  );

  return {
    canvasRef,
    isSaving,
    handleSave,
  };
}
